import React, { useState, useEffect } from "react";
import { Save, Copy, Eye, EyeOff, ShieldCheck, Zap, Globe } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const SlackConfig = ({ activeAgentId }) => {
  const { showToast } = useToast();
  const [slackConfig, setSlackConfig] = useState({
    bot_token: "",
    signing_secret: "",
    is_active: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const response = await ApiClient.request("/slack/config");
        if (response.success && response.config) {
          setSlackConfig({
            bot_token: response.config.bot_token ? UNCHANGED_KEY : "",
            signing_secret: response.config.signing_secret ? UNCHANGED_KEY : "",
            is_active: response.config.is_active || false,
          });
          setWebhookUrl(response.config.webhook_url || "");
        }
      } catch (error) {
        console.log("Slack config not found");
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [activeAgentId]);

  const saveConfig = async () => {
    if (!slackConfig.bot_token.trim()) {
      showToast("Please enter your Bot Token", "error");
      return;
    }
    if (!slackConfig.signing_secret.trim()) {
      showToast("Please enter your Signing Secret", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/slack/config", {
        method: "PUT",
        body: JSON.stringify({
          bot_token: slackConfig.bot_token,
          signing_secret: slackConfig.signing_secret,
        }),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Slack configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    try {
      const newActiveState = !slackConfig.is_active;
      const response = await ApiClient.request("/slack/config", {
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setSlackConfig((prev) => ({ ...prev, is_active: response.active }));
        showToast(response.message, "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  const copyWebhookUrl = () => {
    if (displayUrl) {
      navigator.clipboard.writeText(displayUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };



  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      if (baseUrl) {
        const urlObj = new URL(webhookUrl);
        const path = urlObj.pathname + urlObj.search;
        if (baseUrl.endsWith('/api') && path.startsWith('/api')) {
           return baseUrl.replace('/api', '') + path;
        }
        return baseUrl + path;
      }
    } catch (e) {}
    return webhookUrl;
  };

  const displayUrl = getDisplayWebhookUrl();

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader 
          title="Slack Sync"
          description="Empower your Slack workspace with real-time neural intelligence and automated workflows."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${slackConfig.is_active ? "text-[#83bbe1]" : "text-gray-300"}`}>
                {slackConfig.is_active ? "Operational" : "Offline"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={slackConfig.is_active}
                  onChange={toggleActive}
                  className="sr-only peer"
                />
                <div className="w-12 h-7 bg-gray-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-200 after:border after:rounded-full after:h-[19px] after:w-[19px] after:transition-all peer-checked:bg-[#83bbe1] shadow-inner"></div>
              </label>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Main Configuration Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Slack App Credentials</h3>
                <p className="text-[11px] font-medium text-gray-400">Securely connect your Slack application</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Bot Token */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Bot User OAuth Token
                </label>
                <div className="relative group">
                  <input
                    type={showToken ? "text" : "password"}
                    value={slackConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : slackConfig.bot_token}
                    onChange={(e) =>
                      setSlackConfig({ ...slackConfig, bot_token: e.target.value })
                    }
                    onFocus={(e) => {
                      if (slackConfig.bot_token === UNCHANGED_KEY) {
                        setSlackConfig({ ...slackConfig, bot_token: "" });
                      }
                    }}
                    placeholder={slackConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : "xoxb-your-bot-token"}
                    className="w-full px-5 py-4 pr-12 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  Found under <span className="font-bold">OAuth & Permissions</span> in your Slack App settings.
                </p>
              </div>

              {/* Signing Secret */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Signing Secret
                </label>
                <div className="relative group">
                  <input
                    type={showSecret ? "text" : "password"}
                    value={slackConfig.signing_secret === UNCHANGED_KEY ? MASK_DISPLAY : slackConfig.signing_secret}
                    onChange={(e) =>
                      setSlackConfig({
                        ...slackConfig,
                        signing_secret: e.target.value,
                      })
                    }
                    onFocus={(e) => {
                      if (slackConfig.signing_secret === UNCHANGED_KEY) {
                        setSlackConfig({ ...slackConfig, signing_secret: "" });
                      }
                    }}
                    placeholder={slackConfig.signing_secret === UNCHANGED_KEY ? MASK_DISPLAY : "Your app signing secret"}
                    className="w-full px-5 py-4 pr-12 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  Found under <span className="font-bold">Basic Information</span> in your Slack App credentials.
                </p>
              </div>

              <div className="pt-4 border-t border-gray-50 flex justify-end">
                <button
                  onClick={saveConfig}
                  disabled={saving || !slackConfig.bot_token?.trim() || !slackConfig.signing_secret?.trim()}
                  className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm"
                >
                  <Save size={18} />
                  {saving ? "Saving..." : "Save Slack Config"}
                </button>
              </div>
            </div>
          </div>

          {/* Webhook Card */}
          {webhookUrl && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideUp">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Globe size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Event Subscriptions</h3>
                  <p className="text-[11px] font-medium text-gray-400">Configure your Slack app to send events here</p>
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-[24px] border-2 border-dashed border-gray-200 group transition-all hover:border-blue-200 hover:bg-blue-50/10">
                <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Request URL
                </label>
                <div className="flex items-center justify-between gap-4">
                  <div className="font-mono text-xs text-gray-600 truncate flex-1">
                    {displayUrl}
                  </div>
                  <button
                    onClick={copyWebhookUrl}
                    className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-900 hover:text-white rounded-xl text-[10px] font-bold text-gray-500 transition-all border border-gray-100 shadow-sm"
                  >
                    <Copy size={12} />
                    {copied ? "Copied!" : "Copy URL"}
                  </button>
                </div>
              </div>
              
              <div className="mt-6 space-y-3">
                <div className="flex items-start gap-3 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50">
                  <Zap size={16} className="text-blue-600 mt-0.5" />
                  <p className="text-xs text-blue-800 leading-relaxed">
                    Paste this in <span className="font-bold">Event Subscriptions → Request URL</span> and subscribe to <span className="font-bold">message.channels</span> and <span className="font-bold">message.im</span> bot events.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SlackConfig;
